import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { MapPin, Menu, X, Shield, Instagram, Facebook } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../lib/firebase';

const ADMIN_EMAIL = 'rigvedreddy.gaddam@gmail.com';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  const isAdmin = user?.email === ADMIN_EMAIL;

  const handleLinkClick = (path: string) => {
    setIsOpen(false);
    if (pathname === path) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-agora-cream/80 backdrop-blur-md border-b border-agora-ink/5">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" onClick={() => handleLinkClick('/')} className="flex items-center gap-2 group">
          <div className="w-10 h-10 bg-agora-rust rounded-full flex items-center justify-center transform group-hover:rotate-12 transition-transform">
            <span className="text-white font-serif text-xl font-bold">A</span>
          </div>
          <div>
            <span className="block font-serif text-xl font-bold tracking-tight">The Local Agora</span>
            <span className="block text-[10px] uppercase tracking-[0.2em] opacity-50 font-medium font-sans">Discovery Marketplace</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {isAdmin && (
            <Link to="/admin" onClick={() => handleLinkClick('/admin')} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-agora-rust hover:opacity-70 transition-opacity">
              <Shield size={14} /> Admin
            </Link>
          )}
          
          {/* Social Links on the left of Home */}
          <div className="flex items-center gap-4 border-r border-agora-ink/10 pr-6 mr-1">
            <a 
              id="local-agora-instagram-link"
              href="https://www.instagram.com/the.local.agora/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#E1306C] hover:scale-110 hover:opacity-80 active:scale-95 transition-all duration-200"
              title="Local Agora Instagram"
              aria-label="Visit us on Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a 
              id="local-agora-facebook-link"
              href="https://www.facebook.com/profile.php?id=61590451802723" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#1877F2] hover:scale-110 hover:opacity-80 active:scale-95 transition-all duration-200"
              title="Local Agora Facebook"
              aria-label="Visit us on Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>

          <Link to="/" onClick={() => handleLinkClick('/')} className="text-sm font-medium uppercase tracking-wider hover:text-agora-rust transition-colors">Home</Link>
          <Link to="/about" onClick={() => handleLinkClick('/about')} className="text-sm font-medium uppercase tracking-wider hover:text-agora-rust transition-colors">About</Link>
          <Link to="/events" onClick={() => handleLinkClick('/events')} className="text-sm font-medium uppercase tracking-wider hover:text-agora-rust transition-colors">Events</Link>
          <Link to="/join" onClick={() => handleLinkClick('/join')} className="px-6 py-2 bg-agora-ink text-white rounded-full text-sm font-medium uppercase tracking-wider hover:bg-agora-rust transition-colors">
            Get Featured
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="md:hidden p-2" 
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-20 left-0 right-0 bg-agora-cream border-b border-agora-ink/5 p-6 space-y-4"
        >
          {isAdmin && (
            <Link to="/admin" onClick={() => handleLinkClick('/admin')} className="flex items-center gap-2 text-agora-rust font-bold uppercase tracking-widest text-sm">
              <Shield size={14} /> Admin Dashboard
            </Link>
          )}
          <Link to="/" onClick={() => handleLinkClick('/')} className="block text-lg font-serif">Home</Link>
          <Link to="/about" onClick={() => handleLinkClick('/about')} className="block text-lg font-serif">About</Link>
          <Link to="/events" onClick={() => handleLinkClick('/events')} className="block text-lg font-serif">Events</Link>
          <Link to="/join" onClick={() => handleLinkClick('/join')} className="block text-lg font-serif text-agora-rust">Get Featured</Link>
          <div className="flex items-center gap-6 pt-4 border-t border-agora-ink/5">
            <a 
              id="local-agora-mobile-instagram-link"
              href="https://www.instagram.com/the.local.agora/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#E1306C] hover:scale-110 hover:opacity-80 active:scale-95 transition-all duration-200"
              title="Local Agora Instagram"
              aria-label="Visit us on Instagram"
            >
              <Instagram className="w-6 h-6" />
            </a>
            <a 
              id="local-agora-mobile-facebook-link"
              href="https://www.facebook.com/profile.php?id=61590451802723" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#1877F2] hover:scale-110 hover:opacity-80 active:scale-95 transition-all duration-200"
              title="Local Agora Facebook"
              aria-label="Visit us on Facebook"
            >
              <Facebook className="w-6 h-6" />
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
}
